import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LocationSelector } from '@/components/LocationSelector';
import { CriteriaSelector } from '@/components/CriteriaSelector';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Header } from '@/components/Header';
import ProposalForm from '@/components/ProposalForm';
import { ChevronDown, ChevronUp, Home, MapPin, DollarSign, Layers, Search, RotateCcw, Mail, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config';

const DashboardVendeur = () => {
  const navigate = useNavigate();
  const [acquirerAds, setAcquirerAds] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [loginAction, setLoginAction] = useState('');
  const [userType, setUserType] = useState('vendeur');
  const [loginForm, setLoginForm] = useState({ email: '', password: '', name: '' });
  const [isSignup, setIsSignup] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    property_type: '',
    location_filter: null,
    min_budget: '',
    max_budget: '',
    rooms_min: '',
    rooms_max: '',
    criteria: {}
  });
  const [showAdvancedCriteria, setShowAdvancedCriteria] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
      if (window.scrollY > 10) {
        setShowFilters(true);
      }
    };
    
    const timer = setTimeout(() => {
      setShowFilters(true);
    }, 5000);
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    checkAuth();
    
    const savedFilters = localStorage.getItem('omero_filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(parsed);
        fetchAds(parsed);
      } catch (e) {
        fetchAds();
      }
    } else {
      fetchAds();
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setCurrentUser(response.data);
    } catch (error) {
      setCurrentUser(null);
    }
  };

  const fetchAds = async (customFilters = null) => {
    try {
      const filtersToUse = customFilters || filters;
      
      localStorage.setItem('omero_filters', JSON.stringify(filtersToUse));
      
      const params = {};
      
      if (filtersToUse.property_type && filtersToUse.property_type !== 'all') {
        params.property_type = filtersToUse.property_type;
      }
      if (filtersToUse.min_budget) params.min_budget = filtersToUse.min_budget;
      if (filtersToUse.max_budget) params.max_budget = filtersToUse.max_budget;
      if (filtersToUse.rooms_min) params.rooms_min = filtersToUse.rooms_min;
      if (filtersToUse.rooms_max) params.rooms_max = filtersToUse.rooms_max;
      
      if (filtersToUse.location_filter?.coords) {
        params.lat = filtersToUse.location_filter.coords[0];
        params.lon = filtersToUse.location_filter.coords[1];
      }
      
      if (filtersToUse.criteria && Object.keys(filtersToUse.criteria).length > 0) {
        params.criteria = JSON.stringify(filtersToUse.criteria);
      }

      const response = await axios.get(`${API}/acquirer-ads`, { params });
      setAcquirerAds(response.data);
    } catch (error) {
      console.error('Error:', error);
      setAcquirerAds([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePropose = (ad) => {
    if (!currentUser) {
      setLoginAction('propose');
      setUserType('vendeur');
      setShowLoginModal(true);
    } else {
      setSelectedAd(ad);
      setShowProposalForm(true);
    }
  };

  const handleCreateAd = () => {
    if (!currentUser) {
      setLoginAction('create');
      setUserType('acquereur');
      setShowLoginModal(true);
    } else {
      navigate('/create-search');
    }
  };

  const handleLogin = () => {
    setLoginAction('login');
    setShowLoginModal(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setCurrentUser(null);
      toast.success('Déconnexion réussie');
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleLoginSubmit = async () => {
    try {
      if (isSignup) {
        if (!loginForm.email || !loginForm.name || !loginForm.password) {
          toast.error('Veuillez remplir tous les champs');
          return;
        }
        
        const response = await axios.post(`${API}/auth/signup`, {
          email: loginForm.email,
          name: loginForm.name,
          password: loginForm.password,
          user_type: userType
        }, { withCredentials: true });
        
        // Fallback localStorage pour iOS
        if (response.data.session_token) {
          console.log('[Login] Storing token in localStorage:', response.data.session_token.substring(0, 20));
          localStorage.setItem('session_token', response.data.session_token);
          
          // Vérifier que c'est bien stocké
          const stored = localStorage.getItem('session_token');
          console.log('[Login] Token stored successfully:', stored ? 'YES' : 'NO');
        }
        
        toast.success('Compte créé !');
        setShowLoginModal(false);
        setTimeout(() => {
          console.log('[Login] Reloading page...');
          window.location.reload();
        }, 500);
        
      } else {
        if (!loginForm.email || !loginForm.password) {
          toast.error('Veuillez remplir tous les champs');
          return;
        }
        
        const response = await axios.post(`${API}/auth/login`, {
          email: loginForm.email,
          password: loginForm.password
        }, { withCredentials: true });
        
        // Fallback localStorage pour iOS
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
        
        toast.success('Connexion réussie !');
        setShowLoginModal(false);
        setTimeout(() => window.location.reload(), 500);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 
                       error.message || 
                       'Erreur de connexion. V\u00e9rifiez votre connexion internet.';
      toast.error(errorMsg);
    }
  };

  const handleReset = () => {
    const resetFilters = {
      property_type: '',
      location_filter: null,
      min_budget: '',
      max_budget: '',
      rooms_min: '',
      rooms_max: '',
      criteria: {}
    };
    setFilters(resetFilters);
    setShowAdvancedCriteria(false);
    setLoading(true);
    localStorage.removeItem('omero_filters');
    fetchAds(resetFilters);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        currentUser={currentUser}
        acquirerAdsCount={acquirerAds.length}
        onLoginClick={handleLogin}
        onAboutClick={() => setShowAboutModal(true)}
        onCreateAdClick={handleCreateAd}
        onLogout={handleLogout}
      />

      {/* Hero responsive */}
      <div className="relative min-h-[60vh] md:min-h-[100vh]">
        <div 
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1757439401991-2c6c3df38349?q=85)',
            opacity: showFilters ? 0.3 : 1
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className={`relative h-full min-h-[60vh] md:min-h-screen flex items-center justify-center transition-opacity duration-700 ${
          showFilters ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}>
          <div className="text-center text-white max-w-4xl px-4 md:px-6">
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-3 md:mb-4 tracking-wide">
              OMERO
            </h1>
            <p className="font-body text-lg md:text-2xl lg:text-3xl mb-4 md:mb-8 opacity-90">
              L'immobilier inversé C2C
            </p>
            <p className="font-body text-sm md:text-lg opacity-80 max-w-2xl mx-auto px-4">
              Trouvez les acquéreurs parfaits pour vos biens d'exception
            </p>
          </div>
        </div>

        {/* Filtres en overlay qui apparaissent */}
        <div className={`absolute inset-0 flex items-center justify-center p-4 transition-all duration-700 ${
          showFilters ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}>
          <div className="w-full max-w-3xl">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-border p-4 md:p-8 max-h-[85vh] overflow-y-auto">
            <h2 className="font-display text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">Recherche d'acquéreurs</h2>

            <div className="space-y-3 md:space-y-4">
              <div>
                <Label className="text-xs md:text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <Home className="w-3 h-3 md:w-4 md:h-4" />
                  Type de bien
                </Label>
                <select
                  value={filters.property_type}
                  onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
                  className="w-full rounded border border-input bg-white px-3 py-2 md:py-2.5 text-sm"
                >
                  <option value="">Tous</option>
                  <option value="Maison">Maison</option>
                  <option value="Appartement">Appartement</option>
                </select>
              </div>

              <div>
                <Label className="text-xs md:text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <MapPin className="w-3 h-3 md:w-4 md:h-4" />
                  Localisation
                </Label>
                <LocationSelector
                  location={filters.location_filter}
                  showRadius={false}
                  showMap={false}
                  onChange={(locationData) => setFilters({ ...filters, location_filter: locationData })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div>
                  <Label className="text-xs md:text-sm font-medium text-foreground mb-2 flex items-center gap-1 md:gap-2">
                    <DollarSign className="w-3 h-3 md:w-4 md:h-4" />
                    Budget Min
                  </Label>
                  <Input
                    type="number"
                    value={filters.min_budget}
                    onChange={(e) => setFilters({ ...filters, min_budget: e.target.value })}
                    className="rounded border text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs md:text-sm font-medium text-foreground mb-2 flex items-center gap-1 md:gap-2">
                    <DollarSign className="w-3 h-3 md:w-4 md:h-4" />
                    Budget Max
                  </Label>
                  <Input
                    type="number"
                    value={filters.max_budget}
                    onChange={(e) => setFilters({ ...filters, max_budget: e.target.value })}
                    className="rounded border text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <div>
                  <Label className="text-xs md:text-sm font-medium text-foreground mb-2 flex items-center gap-1 md:gap-2">
                    <Layers className="w-3 h-3 md:w-4 md:h-4" />
                    Pièces Min
                  </Label>
                  <Input
                    type="number"
                    value={filters.rooms_min}
                    onChange={(e) => setFilters({ ...filters, rooms_min: e.target.value })}
                    className="rounded border text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs md:text-sm font-medium text-foreground mb-2 flex items-center gap-1 md:gap-2">
                    <Layers className="w-3 h-3 md:w-4 md:h-4" />
                    Pièces Max
                  </Label>
                  <Input
                    type="number"
                    value={filters.rooms_max}
                    onChange={(e) => setFilters({ ...filters, rooms_max: e.target.value })}
                    className="rounded border text-sm"
                  />
                </div>
              </div>

              <Button
                onClick={() => setShowAdvancedCriteria(!showAdvancedCriteria)}
                variant="outline"
                className="w-full border-2 border-dashed text-sm"
              >
                {showAdvancedCriteria ? <ChevronUp className="w-3 h-3 md:w-4 md:h-4 mr-2" /> : <ChevronDown className="w-3 h-3 md:w-4 md:h-4 mr-2" />}
                {showAdvancedCriteria ? 'Masquer' : '+ de critères'}
              </Button>

              {showAdvancedCriteria && (
                <div className="border-t pt-3 md:pt-4">
                  <CriteriaSelector
                    selectedCriteria={filters.criteria}
                    onChange={(criteria) => setFilters({ ...filters, criteria })}
                    propertyType={filters.property_type || 'Appartement'}
                  />
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => { setLoading(true); fetchAds(filters); }}
                  className="flex-1 bg-black hover:bg-black/90 text-white font-semibold py-3 text-sm md:text-base"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Rechercher
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  className="flex-1 border-2 border-black hover:bg-black hover:text-white font-semibold py-3 text-sm md:text-base"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Annonces responsive */}
      <div className={`max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 transition-all duration-1000 ${
        scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
      }`}>
        <div className="bg-white rounded-lg shadow-sm border border-border p-4 md:p-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="font-display text-lg md:text-2xl font-bold text-foreground">Annonces d'acquéreurs</h2>
            <span className="text-xs md:text-sm text-muted-foreground">{acquirerAds.length} annonce{acquirerAds.length > 1 ? 's' : ''}</span>
          </div>

          {acquirerAds.length === 0 ? (
            <div className="text-center py-12 md:py-20">
              <p className="text-muted-foreground mb-4 text-sm md:text-base">Aucune annonce disponible</p>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {acquirerAds.map((ad) => (
                <div
                  key={ad.ad_id}
                  onClick={() => navigate(`/ad/${ad.ad_id}`)}
                  className="border border-border rounded-lg p-4 md:p-6 hover:shadow-lg transition-all bg-white cursor-pointer"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:justify-between md:gap-4 mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading text-base md:text-xl font-bold text-foreground mb-2 md:mb-3">{ad.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="bg-muted text-foreground px-2 md:px-3 py-1 rounded text-xs md:text-sm font-medium">
                          {ad.property_type}
                        </span>
                        <span className="bg-accent/10 text-accent-foreground px-2 md:px-3 py-1 rounded text-xs md:text-sm font-medium flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {ad.location}
                        </span>
                      </div>
                    </div>
                    {(!currentUser || currentUser.user_type === 'vendeur' || currentUser.user_type === 'both') && (
                      <Button 
                        onClick={(e) => { e.stopPropagation(); handlePropose(ad); }}
                        className="w-full md:w-auto bg-black hover:bg-black/90 text-white px-4 md:px-6 py-2.5 text-sm md:text-base flex items-center justify-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="hidden sm:inline">Proposer un bien</span>
                        <span className="sm:hidden">Proposer</span>
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-3 md:mb-4">
                    <div className="bg-muted/30 p-2 md:p-3 rounded">
                      <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Budget
                      </p>
                      <p className="text-xs md:text-sm font-bold text-foreground">
                        {ad.min_budget && `${ad.min_budget.toLocaleString('fr-FR')} - `}
                        {ad.max_budget.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    {ad.financing_type && (
                      <div className="bg-muted/30 p-2 md:p-3 rounded">
                        <p className="text-xs text-muted-foreground mb-1">Finance</p>
                        <p className="text-xs font-semibold truncate">{ad.financing_type.split(' ')[0]}</p>
                      </div>
                    )}
                    {ad.min_surface && (
                      <div className="bg-muted/30 p-2 md:p-3 rounded">
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          Surface
                        </p>
                        <p className="text-xs font-semibold">
                          {ad.min_surface} - {ad.max_surface || ad.min_surface} m²
                        </p>
                      </div>
                    )}
                    {ad.rooms && (
                      <div className="bg-muted/30 p-2 md:p-3 rounded">
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          Pièces
                        </p>
                        <p className="text-xs font-semibold">{ad.rooms} pièces</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Projet</p>
                    <p className="font-body text-xs md:text-sm line-clamp-2">{ad.project_description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAboutModal && (
        <Dialog open={showAboutModal} onOpenChange={setShowAboutModal}>
          <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                Qu'est-ce qu'Omero ?
              </DialogTitle>
              <DialogDescription className="font-body text-base md:text-lg text-muted-foreground">
                Le portail immobilier inversé C2C
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 md:space-y-8 py-4">
              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-2 md:mb-3">Le Concept</h3>
                <p className="font-body text-sm md:text-base text-foreground leading-relaxed">
                  Omero inverse le paradigme traditionnel. Les <strong>acquéreurs créent des annonces</strong> détaillées avec leur projet de vie, et les <strong>vendeurs choisissent</strong> à qui proposer leur bien.
                </p>
              </div>

              <div className="bg-muted/30 p-4 md:p-6 rounded-lg">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground mb-4 md:mb-6">5 Étapes</h3>
                
                <div className="space-y-4 md:space-y-5">
                  <div className="flex gap-3 md:gap-4">
                    <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">1</div>
                    <div>
                      <h4 className="font-heading font-bold text-foreground mb-1 text-sm md:text-base">Acquéreur exprime son besoin</h4>
                      <p className="font-body text-xs md:text-sm text-muted-foreground">Fiche de recherche avec projet de vie détaillé</p>
                    </div>
                  </div>

                  <div className="flex gap-3 md:gap-4">
                    <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">2</div>
                    <div>
                      <h4 className="font-heading font-bold text-foreground mb-1 text-sm md:text-base">Vendeur garde le contrôle</h4>
                      <p className="font-body text-xs md:text-sm text-muted-foreground">Bien jamais affiché publiquement - discrétion totale</p>
                    </div>
                  </div>

                  <div className="flex gap-3 md:gap-4">
                    <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">3</div>
                    <div>
                      <h4 className="font-heading font-bold text-foreground mb-1 text-sm md:text-base">Matching qualifié</h4>
                      <p className="font-body text-xs md:text-sm text-muted-foreground">Vendeur cherche projet de vie compatible</p>
                    </div>
                  </div>

                  <div className="flex gap-3 md:gap-4">
                    <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">4</div>
                    <div>
                      <h4 className="font-heading font-bold text-foreground mb-1 text-sm md:text-base">Proposition exclusive</h4>
                      <p className="font-body text-xs md:text-sm text-muted-foreground">Proposition avec photos uniquement pour l'acquéreur</p>
                    </div>
                  </div>

                  <div className="flex gap-3 md:gap-4">
                    <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">5</div>
                    <div>
                      <h4 className="font-heading font-bold text-foreground mb-1 text-sm md:text-base">Connexion directe</h4>
                      <p className="font-body text-xs md:text-sm text-muted-foreground">Messagerie privée pour discuter et finaliser</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center pt-4 border-t border-border">
                <p className="font-body text-sm md:text-base text-foreground">
                  <strong>Omero</strong> - Un système de matching intelligent pour des transactions immobilières d'exception
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showLoginModal && (
        <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
          <DialogContent className="sm:max-w-md w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-lg md:text-xl">
                {isSignup ? 'Inscription' : 'Connexion'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 md:space-y-4 py-4">
              <Tabs value={userType} onValueChange={setUserType}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="acquereur" className="text-xs md:text-sm">Acquéreur</TabsTrigger>
                  <TabsTrigger value="vendeur" className="text-xs md:text-sm">Vendeur</TabsTrigger>
                </TabsList>
              </Tabs>

              {isSignup && (
                <div>
                  <Label className="text-sm">Nom complet</Label>
                  <Input
                    value={loginForm.name}
                    onChange={(e) => setLoginForm({ ...loginForm, name: e.target.value })}
                    className="text-sm"
                  />
                </div>
              )}

              <div>
                <Label className="text-sm">Email</Label>
                <Input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="text-sm"
                />
              </div>

              <div>
                <Label className="text-sm">Mot de passe</Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="text-sm"
                />
              </div>
              
              <Button 
                onClick={handleLoginSubmit}
                className="w-full bg-black hover:bg-black/90 text-white text-sm"
              >
                {isSignup ? "S'inscrire" : 'Se connecter'}
              </Button>

              <p className="text-center text-xs md:text-sm text-muted-foreground">
                {isSignup ? (
                  <>Déjà compte ? <button onClick={() => setIsSignup(false)} className="text-primary font-semibold">Connexion</button></>
                ) : (
                  <>Pas de compte ? <button onClick={() => setIsSignup(true)} className="text-primary font-semibold">Inscription</button></>
                )}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showProposalForm && selectedAd && (
        <ProposalForm
          ad={selectedAd}
          onClose={() => { setShowProposalForm(false); setSelectedAd(null); }}
          onSuccess={() => {
            setShowProposalForm(false);
            setSelectedAd(null);
            toast.success('Proposition envoyée !');
          }}
        />
      )}
    </div>
  );
};

export default DashboardVendeur;
